import React from 'react';
import { Check, Star } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { useBookersContent } from '../hooks/useBookersContent';
import SignUpLink from './SignUpLink';

export default function Pricing({ cms }) {
  const { t } = useI18n();
  const { packages: fallbackPackages } = useBookersContent();
  const p = cms || {};
  const eyebrow = p.eyebrow || t('pricing.eyebrow');
  const title = p.title || t('pricing.title');
  const description = p.description || t('pricing.description');
  const footerText = p.footer_text || t('pricing.footer');
  const packages = p.packages || fallbackPackages;

  return (
    <section id="pricing" className="mx-auto max-w-7xl px-5 lg:px-8 section-pad">
      <div className="text-center max-w-2xl mx-auto reveal">
        <p className="text-sm font-semibold text-[#76B828]">{eyebrow}</p>
        <h2 className="font-display text-4xl md:text-5xl mt-3 text-[#0b0b0f]">{title}</h2>
        <p className="text-[#55555f] mt-4 text-lg">{description}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-14 items-stretch">
        {packages.map((pkg, i) => (
          <div
            key={pkg.name}
            className={`reveal relative flex flex-col rounded-[2rem] border p-8 transition-all ${
              pkg.popular
                ? 'border-[#0b0b0f] bg-[#0b0b0f] text-white shadow-[0_30px_80px_rgba(15,15,20,0.28)] lg:-translate-y-3'
                : 'border-black/10 bg-white text-[#0b0b0f] hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(15,15,20,0.10)]'
            }`}
            style={{ transitionDelay: `${i * 90}ms` }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                pkg.popular ? 'bg-white/15 text-white' : 'bg-[#EEF6DC] text-[#76B828]'
              }`}>
                {pkg.popular && <Star size={12} className="fill-current" />} {pkg.tag}
              </span>
              {pkg.offer && (
                <span className="inline-flex items-center rounded-full bg-[#16a34a] text-white px-3 py-1 text-xs font-semibold">
                  {pkg.offer}
                </span>
              )}
            </div>
            <h3 className={`font-display text-2xl mt-5 ${pkg.popular ? 'text-white' : 'text-[#0b0b0f]'}`}>{pkg.name}</h3>
            <p className={`mt-1 text-[15px] ${pkg.popular ? 'text-white/70' : 'text-[#6b6b73]'}`}>{pkg.desc}</p>

            <div className="mt-6 flex items-end gap-2">
              <span className="font-display text-5xl">{t('common.chf')} {pkg.price}</span>
              <span className={`mb-2 text-[15px] ${pkg.popular ? 'text-white/60' : 'text-[#8b8b95]'}`}>{t('pricing.perMonth')}</span>
              {pkg.original && (
                <span className={`mb-2 text-[15px] line-through ${pkg.popular ? 'text-white/40' : 'text-[#b7b7c0]'}`}>{t('common.chf')} {pkg.original}</span>
              )}
            </div>

            <SignUpLink
              className={`btn-pill w-full py-3.5 text-[15px] mt-6 text-center ${
                pkg.popular ? 'bg-white text-[#0b0b0f] hover:bg-white/90 font-semibold' : 'btn-dark'
              }`}
            >
              {t('pricing.signUp')}
            </SignUpLink>

            <ul className="mt-7 space-y-3">
              {(pkg.features || []).map((f) => (
                <li key={f} className="flex items-start gap-3 text-[14.5px]">
                  <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${
                    pkg.popular ? 'bg-white/15' : 'bg-[#e9f9f0]'
                  }`}>
                    <Check size={12} className={pkg.popular ? 'text-white' : 'text-[#16a34a]'} />
                  </span>
                  <span className={pkg.popular ? 'text-white/85' : 'text-[#3a3a42]'}>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className="text-center text-sm text-[#8b8b95] mt-8 reveal">
        {footerText}
      </p>
    </section>
  );
}
